import { Component, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { UserService } from '../authentication/services/user.service';
import { of, switchMap } from 'rxjs';
import {
    UserAuth,
    UserProfile,
} from '../authentication/profile/models/user-profile';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { InputFormDialogComponent } from '../shared/components/input-form-modal/input-form-dialog.component';
import { OrganizationService } from './services/organization.service';
import {
    OrganizationDto,
    OrganizationModificationDto,
} from './model/organization';
import { Validators } from '@angular/forms';
import { OrganizationDetailsPageComponent } from './components/organization-details-page/organization-details-page.component';
import { IDeactivateComponent } from '../authentication/i-deactivate/i-deactivate.component';
import { validationPatterns } from '../shared/validation-patterns';
import { Router } from '@angular/router';

@Component({
    selector: 'app-organization-area',
    templateUrl: './organization-area.component.html',
    styleUrls: ['./organization-area.component.scss'],
    standalone: false,
})
export class OrganizationAreaComponent implements OnInit, IDeactivateComponent {
    @ViewChild(OrganizationDetailsPageComponent)
    organizationDetailsPageComponent: OrganizationDetailsPageComponent;
    tabIndex: number = 0;
    userId: string;
    profilePicture: string;
    largeHeadline: string;
    smallHeadlineTranslationKey: string;
    isInAnyOrg: boolean;
    activeOrganization: string;
    matDialogConfig: MatDialogConfig;
    orgId: string;
    hasOrganizationFeature: boolean;
    isOrgSuperAdmin: boolean;

    constructor(
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private matDialog: MatDialog,
        private organizationService: OrganizationService,
        private router: Router
    ) {
        this.matDialogConfig = new MatDialogConfig();
    }

    ngOnInit(): void {
        this.authenticationService
            .observableUserAuth()
            .subscribe((userAuth: UserAuth) => {
                this.userId = userAuth.id;
                this.hasOrganizationFeature =
                    this.authenticationService.hasOrganizationFeatureAccess();
                if (!this.hasOrganizationFeature) {
                    this.router.navigate(['workspace']);
                    return;
                }
                this.activeOrganization = userAuth.activeOrganization;
                this.isOrgSuperAdmin =
                    this.authenticationService.isOrganizationSuperAdmin(
                        this.activeOrganization
                    );
                const organizations = Object.keys(
                    userAuth.organizationProperties
                );
                this.isInAnyOrg = organizations.length > 0;
                if (this.isInAnyOrg) {
                    this.orgId =
                        userAuth.activeOrganization !== ''
                            ? userAuth.activeOrganization
                            : organizations[0];
                    this.largeHeadline =
                        userAuth.organizationProperties[this.orgId].name;
                } else {
                    this.largeHeadline = '';
                }
            });
        this.profilePicture = 'assets/svg/reusable-svg/organization-mode.svg';
        this.updateHeadlineDisplay();
    }
    getTabDescription(): string {
        switch (this.tabIndex) {
            case 0:
                return 'organization.organizationArea.header.details';
            case 1:
                return 'organization.organizationArea.header.member';
            case 2:
                return 'organization.organizationArea.header.subscription';
            case 3:
                return 'organization.organizationArea.header.credits';
            default:
                return '';
        }
    }

    /**
     * Updates the headline display based on current tab.
     * For profile tab (0), shows email; for other tabs, shows translation key.
     */
    updateHeadlineDisplay(): void {
        this.smallHeadlineTranslationKey = this.getTabDescription();
    }

    onSelectTab(tabIndex: number): void {
        this.tabIndex = tabIndex;
        this.updateHeadlineDisplay();
    }

    openOrganizationCreationModal() {
        this.matDialogConfig.data = {
            headerTranslationKey:
                'organization.organizationArea.createOrganization.button',
            submitButtonTranslationKey:
                'organization.organizationArea.createOrganization.button',
            defaultErrorTranslationKey: 'default.errorMessage',
            submitAction: (dto: OrganizationModificationDto) =>
                this.organizationService.createOrganization(dto),
            inputErrorTranslationKeyPrefix:
                'organization.organizationArea.createOrganization.form.errors',
            resolveErrorTranslationKey: (error) => {
                if (error.error.type === 'BadRequestException') {
                    if (
                        error.error.message ===
                        'Organization name already taken.'
                    ) {
                        return 'organization.organizationArea.createOrganization.form.errors.nameAlreadyTaken';
                    } else if (
                        error.error.message ===
                        'User already belongs to an organization.'
                    ) {
                        return 'organization.organizationArea.createOrganization.form.errors.userAlreadyBelongsToOrganization';
                    }
                }
                return 'default.errorMessage';
            },
            fields: [
                {
                    controlName: 'name',
                    labelTranslationKey:
                        'organization.organizationArea.createOrganization.form.name',
                    validators: [
                        Validators.required,
                        Validators.minLength(1),
                        Validators.pattern(
                            validationPatterns.alphaNumericPointMinusUnderScoreBlankPattern
                        ),
                    ],
                    infoTranslationKey:
                        'organization.organizationArea.createOrganization.form.infoNameUnique',
                    maxLength: 30,
                },
                // {
                //     controlName: 'description',
                //     labelTranslationKey:
                //         'organization.organizationArea.createOrganization.form.description',
                //     validators: [
                //         validateAlphaNumericPointMinusUnderScoreBlankAllowed,
                //     ],
                //     maxLength: 50,
                // },
            ],
        };
        this.matDialog
            .open(InputFormDialogComponent, this.matDialogConfig)
            .afterClosed()
            .pipe(
                switchMap((organization: OrganizationDto) => {
                    if (organization?.id) {
                        return this.authenticationService
                            .refreshUserAuth()
                            .pipe(
                                switchMap(() =>
                                    this.userService.updateUser(
                                        {
                                            activeOrganization: organization.id,
                                        } as UserProfile,
                                        this.userId
                                    )
                                ),

                                switchMap(() =>
                                    this.authenticationService.refreshUserAuth()
                                )
                            );
                    }
                    return of(null);
                })
            )
            .subscribe();
    }

    /**
     * Implementation of IDeactivateComponent interface.
     * Delegates to OrganizationDetailsPageComponent if it exists and tab is active.
     */
    canExit(): boolean {
        if (this.tabIndex === 0 && this.organizationDetailsPageComponent) {
            return this.organizationDetailsPageComponent.canExit();
        }
        return true;
    }
}
